import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { useParams, Link, useSearchParams } from 'react-router-dom';
import { ArrowLeft, Download, Loader2, Search, LayoutGrid } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Card } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Checkbox } from '../../components/ui/checkbox';
import { toast } from 'sonner';
import ClientLoanLedgerSummary from './ClientLoanLedgerSummary';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const fmtZAR = (v) =>
  new Intl.NumberFormat('en-ZA', { style: 'currency', currency: 'ZAR', maximumFractionDigits: 2 }).format(Number(v || 0));

const fmtZeroDash = (v) => {
  const n = Number(v || 0);
  if (Math.abs(n) < 0.005) return <span className="text-slate-400">—</span>;
  return fmtZAR(n);
};

// Auth header — read from either storage key so tokens from any login
// path work.
function useAuthHeader() {
  return useMemo(() => {
    const token = sessionStorage.getItem('token') || localStorage.getItem('token');
    return token ? { Authorization: `Bearer ${token}` } : {};
  }, []);
}

export default function AdminClientLoanLedgerPage() {
  const { clientId } = useParams();
  const [searchParams] = useSearchParams();
  const initialLoan = searchParams.get('loan') || '';

  const authHeader = useAuthHeader();

  const [loans, setLoans] = useState([]);
  const [selected, setSelected] = useState(new Set(initialLoan ? [initialLoan] : []));
  const [filter, setFilter] = useState('');
  const [ledger, setLedger] = useState(null);
  const [loadingList, setLoadingList] = useState(false);
  const [loadingLedger, setLoadingLedger] = useState(false);
  const [payments, setPayments] = useState([]);
  const [loadingPayments, setLoadingPayments] = useState(false);
  const [expandedPayment, setExpandedPayment] = useState(null);

  const fetchLoans = useCallback(async () => {
    try {
      setLoadingList(true);
      const r = await axios.get(
        `${API_URL}/api/admin/client-loan-ledger/loans/${encodeURIComponent(clientId)}`,
        { headers: authHeader }
      );
      setLoans(r.data?.items || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load loans for client');
    } finally {
      setLoadingList(false);
    }
  }, [clientId, authHeader]);

  const fetchLedger = useCallback(async () => {
    if (selected.size === 0) {
      setLedger(null);
      return;
    }
    try {
      setLoadingLedger(true);
      const loanNumbers = Array.from(selected).join(',');
      const r = await axios.get(
        `${API_URL}/api/admin/client-loan-ledger/${encodeURIComponent(clientId)}`,
        { headers: authHeader, params: { loan_numbers: loanNumbers } }
      );
      setLedger(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to build ledger');
    } finally {
      setLoadingLedger(false);
    }
  }, [clientId, authHeader, selected]);

  const fetchPayments = useCallback(async () => {
    try {
      setLoadingPayments(true);
      const r = await axios.get(
        `${API_URL}/api/admin/client-loan-ledger/${encodeURIComponent(clientId)}/payment-allocations`,
        { headers: authHeader }
      );
      setPayments(r.data?.payments || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load payment allocations');
    } finally {
      setLoadingPayments(false);
    }
  }, [clientId, authHeader]);

  useEffect(() => { fetchLoans(); }, [fetchLoans]);
  useEffect(() => { fetchLedger(); }, [fetchLedger]);
  useEffect(() => { fetchPayments(); }, [fetchPayments]);

  const toggleOne = (ln) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(ln)) next.delete(ln); else next.add(ln);
      return next;
    });
  };

  const toggleAllVisible = (loansVisible) => {
    setSelected((prev) => {
      const allIn = loansVisible.every((l) => prev.has(l.loan_number));
      const next = new Set(prev);
      if (allIn) {
        loansVisible.forEach((l) => next.delete(l.loan_number));
      } else {
        loansVisible.forEach((l) => next.add(l.loan_number));
      }
      return next;
    });
  };

  const visibleLoans = useMemo(() => {
    const q = filter.trim().toLowerCase();
    if (!q) return loans;
    return loans.filter((l) =>
      l.loan_number.toLowerCase().includes(q) || (l.customer || '').toLowerCase().includes(q)
    );
  }, [loans, filter]);

  const exportXlsx = async () => {
    if (selected.size === 0) {
      toast.error('Select at least one loan to export');
      return;
    }
    try {
      const loanNumbers = Array.from(selected).join(',');
      const r = await axios.get(
        `${API_URL}/api/admin/client-loan-ledger/${encodeURIComponent(clientId)}/export.xlsx`,
        { headers: authHeader, params: { loan_numbers: loanNumbers }, responseType: 'blob' }
      );
      const blob = new Blob([r.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `loan-ledger-${clientId}-${new Date().toISOString().slice(0, 10)}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error('Excel export failed');
    }
  };

  // Resolve display client name using the same rules as the backend
  // summary endpoint: prefer client_profiles.company_name (which flows
  // through the ledger/summary response), then a non-"VARIOUS" LB
  // customer field, and only fall back to the raw client_id.
  const clientName = useMemo(() => {
    const fromLedger = ledger?.client_name;
    if (fromLedger && fromLedger.toUpperCase() !== 'VARIOUS') return fromLedger;
    const nonVarious = (loans || []).find(
      (l) => l.customer && String(l.customer).toUpperCase() !== 'VARIOUS'
    );
    if (nonVarious) return nonVarious.customer;
    return clientId;
  }, [ledger, loans, clientId]);

  return (
    <div className="min-h-screen bg-slate-50 text-slate-900 px-6 py-6" data-testid="client-loan-ledger-page">
      <div className="max-w-[1400px] mx-auto space-y-6">
        {/* Top bar */}
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <Link
              to="/admin/clients"
              className="inline-flex items-center gap-1 text-sm text-slate-600 hover:text-slate-900"
              data-testid="back-to-clients-link"
            >
              <ArrowLeft className="h-4 w-4" /> Back to Clients
            </Link>
            <h1 className="mt-1 text-2xl font-semibold text-slate-900">
              Loan Ledger — <span className="text-emerald-700">{clientName}</span>
            </h1>
            <div className="text-xs text-slate-500">Client ID: {clientId}</div>
          </div>
          <Button
            onClick={exportXlsx}
            disabled={selected.size === 0}
            className="bg-emerald-600 hover:bg-emerald-500 text-white"
            data-testid="export-xlsx-btn"
          >
            <Download className="h-4 w-4 mr-2" />
            Export XLSX
          </Button>
        </div>

        <ClientPaymentAllocations
          payments={payments}
          loading={loadingPayments}
          expanded={expandedPayment}
          setExpanded={setExpandedPayment}
        />

        <div className="grid grid-cols-1 lg:grid-cols-[320px,1fr] gap-6">
          {/* Left rail: loan number picker */}
          <Card className="bg-white border-slate-200 p-4 space-y-3 shadow-sm" data-testid="loan-selector-card">
            <div className="flex items-center justify-between">
              <div className="text-sm font-medium text-slate-800">Loan numbers</div>
              <div className="text-xs text-slate-500">{selected.size} selected · {loans.length} total</div>
            </div>
            <div className="relative">
              <Search className="h-4 w-4 absolute left-2 top-2.5 text-slate-500" />
              <Input
                value={filter}
                onChange={(e) => setFilter(e.target.value)}
                placeholder="Filter…"
                className="pl-8"
                data-testid="loan-filter-input"
              />
            </div>
            <div className="flex items-center gap-2 text-xs">
              <button
                onClick={() => toggleAllVisible(visibleLoans)}
                className="text-emerald-700 hover:underline"
                data-testid="toggle-all-loans-btn"
              >
                {visibleLoans.every((l) => selected.has(l.loan_number)) && visibleLoans.length > 0
                  ? 'Deselect all shown'
                  : 'Select all shown'}
              </button>
              {selected.size > 0 && (
                <button
                  onClick={() => setSelected(new Set())}
                  className="text-slate-500 hover:underline"
                  data-testid="clear-selection-btn"
                >
                  Clear
                </button>
              )}
            </div>
            <div className="max-h-[70vh] overflow-y-auto pr-1 space-y-1">
              {loadingList && <div className="text-sm text-slate-500 p-2">Loading…</div>}
              {!loadingList && visibleLoans.length === 0 && (
                <div className="text-sm text-slate-500 p-2">No loans found for this client.</div>
              )}
              {visibleLoans.map((l) => {
                const checked = selected.has(l.loan_number);
                return (
                  <label
                    key={l.loan_number}
                    className={`flex items-center gap-3 px-2 py-2 rounded cursor-pointer border ${
                      checked ? 'border-emerald-500 bg-emerald-50' : 'border-transparent hover:bg-slate-100'
                    }`}
                    data-testid={`loan-row-${l.loan_number}`}
                  >
                    <Checkbox
                      checked={checked}
                      onCheckedChange={() => toggleOne(l.loan_number)}
                      data-testid={`loan-cb-${l.loan_number}`}
                    />
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-mono text-slate-900">{l.loan_number}</div>
                      <div className="text-xs text-slate-500 truncate">{l.customer || '—'}</div>
                    </div>
                    <div className="text-right shrink-0">
                      <div className="text-xs text-slate-600">{l.tranche_count} tr.</div>
                      <div className="text-[10px] text-slate-500">{fmtZAR(l.outstanding)}</div>
                    </div>
                  </label>
                );
              })}
            </div>
          </Card>

          {/* Right: ledger tables */}
          <div className="space-y-6" data-testid="ledger-tables-area">
            {loadingLedger && (
              <Card className="bg-white border-slate-200 p-6 flex items-center gap-3 text-slate-700 shadow-sm">
                <Loader2 className="h-4 w-4 animate-spin" /> Loading ledger…
              </Card>
            )}
            {!loadingLedger && selected.size === 0 && (
              <div className="space-y-4">
                <Card className="bg-emerald-50/50 border-emerald-200 p-3 text-xs text-emerald-900 flex items-center gap-2 shadow-sm">
                  <LayoutGrid className="h-4 w-4" />
                  Summary of every loan for {clientName}. Click any row to open its daily ledger, or pick loans from the left rail for multi-loan view.
                </Card>
                <ClientLoanLedgerSummary
                  clientId={clientId}
                  onOpenLoan={(ln) => setSelected(new Set([ln]))}
                />
              </div>
            )}
            {!loadingLedger &&
              (ledger?.loans || []).map((loan) => (
                <LoanLedgerBlock key={loan.loan_number} loan={loan} />
              ))}
          </div>
        </div>
      </div>
    </div>
  );
}

// Only ever shows repayments that were actually matched to a real Main
// Account bank transaction (see services/client_bank_reconciliation.py) -
// internal rebalancing entries never appear here by construction, since
// they never have a bank match. A payment recorded before the
// interest/principal/VAT waterfall existed shows those columns as "—"
// rather than a misleading 0.00.
function ClientPaymentAllocations({ payments, loading, expanded, setExpanded }) {
  const fmtOrDash = (v) => (v === null || v === undefined ? <span className="text-slate-400">—</span> : fmtZAR(v));

  return (
    <Card className="bg-white border-slate-200 shadow-sm" data-testid="client-payments-card">
      <div className="px-4 py-3 border-b border-slate-200">
        <div className="text-sm font-semibold text-slate-900">Client Payments</div>
        <div className="text-xs text-slate-500">
          Every repayment recorded for this client — which loan(s) received which portion, and how much covered interest, principal, or VAT. "Bank-matched" shows where the Main Account line has also been matched.
        </div>
      </div>
      {loading && (
        <div className="px-4 py-6 flex items-center gap-2 text-sm text-slate-500">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading payments…
        </div>
      )}
      {!loading && payments.length === 0 && (
        <div className="px-4 py-6 text-sm text-slate-500">
          No repayments have been recorded in the portal for this client yet.
        </div>
      )}
      {!loading && payments.length > 0 && (
        <div className="divide-y divide-slate-100">
          {payments.map((p) => {
            const isOpen = expanded === p.batch_id;
            return (
              <div key={p.batch_id} data-testid={`payment-row-${p.batch_id}`}>
                <button
                  type="button"
                  onClick={() => setExpanded(isOpen ? null : p.batch_id)}
                  className="w-full flex items-center justify-between gap-3 px-4 py-3 text-left hover:bg-slate-50"
                  data-testid={`payment-toggle-${p.batch_id}`}
                >
                  <div className="flex items-center gap-3 min-w-0">
                    <span className="font-mono text-sm text-slate-700">{p.repayment_date}</span>
                    {p.bank_matched ? (
                      <span className="text-[10px] text-emerald-700 bg-emerald-50 border border-emerald-200 rounded px-1">Bank-matched</span>
                    ) : null}
                    <span className="text-xs text-slate-500 truncate max-w-[320px]" title={p.bank_description || ''}>
                      {p.bank_description || ''}
                    </span>
                  </div>
                  <div className="flex items-center gap-4 shrink-0">
                    <span className="font-semibold text-slate-900">{fmtZAR(p.total_applied)}</span>
                    <span className="text-xs text-slate-500">{p.loans.length} loan{p.loans.length === 1 ? '' : 's'}</span>
                  </div>
                </button>
                {isOpen && (
                  <div className="px-4 pb-4">
                    <table className="w-full text-xs border border-slate-200 rounded overflow-hidden">
                      <thead className="bg-slate-50 text-slate-600 uppercase tracking-wide">
                        <tr>
                          <th className="text-left px-3 py-2">Loan</th>
                          <th className="text-right px-3 py-2">Applied</th>
                          <th className="text-right px-3 py-2">VAT</th>
                          <th className="text-right px-3 py-2">Interest</th>
                          <th className="text-right px-3 py-2">Principal</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100">
                        {p.loans.map((l) => (
                          <tr key={l.loan_id}>
                            <td className="px-3 py-2 font-mono text-slate-700">
                              {l.loan_id}
                              {l.is_fee_tranche && <span className="ml-1 text-[10px] text-amber-700 bg-amber-50 border border-amber-200 rounded px-1">FEE</span>}
                            </td>
                            <td className="px-3 py-2 text-right text-slate-900">{fmtZAR(l.amount_applied)}</td>
                            <td className="px-3 py-2 text-right text-slate-900">{fmtOrDash(l.vat_paid)}</td>
                            <td className="px-3 py-2 text-right text-slate-900">{fmtOrDash(l.interest_paid)}</td>
                            <td className="px-3 py-2 text-right text-slate-900">{fmtOrDash(l.principal_paid)}</td>
                          </tr>
                        ))}
                      </tbody>
                      <tfoot className="bg-slate-50 border-t border-slate-200">
                        <tr className="font-semibold text-slate-900">
                          <td className="px-3 py-2">Total</td>
                          <td className="px-3 py-2 text-right">{fmtZAR(p.total_applied)}</td>
                          <td className="px-3 py-2 text-right">{fmtZAR(p.total_vat_paid)}</td>
                          <td className="px-3 py-2 text-right">{fmtZAR(p.total_interest_paid)}</td>
                          <td className="px-3 py-2 text-right">{fmtZAR(p.total_principal_paid)}</td>
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </Card>
  );
}

function LoanLedgerBlock({ loan }) {
  return (
    <Card
      className="bg-white border-slate-200 overflow-hidden shadow-sm"
      data-testid={`ledger-block-${loan.loan_number}`}
    >
      {/* Header band */}
      <div className="bg-gradient-to-r from-emerald-100 via-emerald-50 to-white px-6 py-4 border-b border-slate-200">
        <div className="flex flex-wrap items-baseline justify-between gap-3">
          <div>
            <div className="text-xs uppercase tracking-widest text-emerald-700">Loan</div>
            <div className="text-3xl font-semibold font-mono text-slate-900">{loan.loan_number}</div>
          </div>
          <div className="text-right">
            <div className="text-xs uppercase tracking-widest text-slate-500">Client</div>
            <div className="text-lg font-medium text-slate-900">{loan.client_name}</div>
          </div>
          <div className="text-right">
            <div className="text-xs uppercase tracking-widest text-slate-500">Interest Rate</div>
            <div className="text-lg font-semibold text-emerald-700">
              {Number(loan.interest_rate_pct || 0).toFixed(2)}%
            </div>
            <div className="text-[11px] text-slate-500 mt-1">
              {loan.tranche_count} tranche{loan.tranche_count === 1 ? '' : 's'}
              {loan.fee_tranche_count > 0 && ` · ${loan.fee_tranche_count} FEE line${loan.fee_tranche_count === 1 ? '' : 's'}`}
            </div>
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="text-xs uppercase tracking-wider text-slate-600 bg-slate-100 border-b border-slate-200">
            <tr>
              <th className="text-left px-4 py-2">Date</th>
              <th className="text-right px-4 py-2">Capital Deployed (ZAR)</th>
              <th className="text-right px-4 py-2">Fees</th>
              <th className="text-right px-4 py-2">VAT on Fees</th>
              <th className="text-right px-4 py-2">Interest (ZAR)</th>
              <th className="text-right px-4 py-2">Cap. and Interest Repaid</th>
              <th className="text-right px-4 py-2">Closing Balance (ZAR)</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {(loan.rows || []).map((r) => (
              <tr
                key={r.date}
                className="hover:bg-emerald-50/40"
                data-testid={`ledger-row-${loan.loan_number}-${r.date}`}
              >
                <td className="px-4 py-2 font-mono text-slate-700">{r.date}</td>
                <td className="px-4 py-2 text-right text-slate-900">{fmtZeroDash(r.capital_deployed)}</td>
                <td className="px-4 py-2 text-right text-slate-900">{fmtZeroDash(r.fees)}</td>
                <td className="px-4 py-2 text-right text-slate-900">{fmtZeroDash(r.vat_on_fees)}</td>
                <td className="px-4 py-2 text-right text-slate-900">{fmtZeroDash(r.interest)}</td>
                <td className="px-4 py-2 text-right text-slate-900">{fmtZeroDash(r.cap_and_interest_repaid)}</td>
                <td className="px-4 py-2 text-right font-medium text-slate-900">{fmtZAR(r.closing_balance)}</td>
              </tr>
            ))}
            {(!loan.rows || loan.rows.length === 0) && (
              <tr>
                <td colSpan="7" className="px-4 py-6 text-center text-slate-500">
                  No cashflows or interest recorded for this loan number.
                </td>
              </tr>
            )}
          </tbody>
          {loan.rows && loan.rows.length > 0 && (
            <tfoot className="bg-slate-100 border-t border-slate-300">
              <tr className="text-slate-900">
                <td className="px-4 py-2 font-semibold">Total</td>
                <td className="px-4 py-2 text-right">{fmtZAR(loan.totals?.capital_deployed)}</td>
                <td className="px-4 py-2 text-right">{fmtZAR(loan.totals?.fees)}</td>
                <td className="px-4 py-2 text-right">{fmtZAR(loan.totals?.vat_on_fees)}</td>
                <td className="px-4 py-2 text-right">{fmtZAR(loan.totals?.interest)}</td>
                <td className="px-4 py-2 text-right">{fmtZAR(loan.totals?.cap_and_interest_repaid)}</td>
                <td className="px-4 py-2 text-right font-semibold text-emerald-700">
                  {fmtZAR(loan.totals?.final_closing_balance)}
                </td>
              </tr>
            </tfoot>
          )}
        </table>
      </div>
    </Card>
  );
}
